import React, { useEffect, useState } from 'react';
import { acordApi, holdersApi, apiErrorMessage } from '../../services/agentApi';
import { btn, input } from '../agent/agentUi';
import CertificateHoldersPanel, { HolderForm, emptyHolder, CertificateHolder } from './CertificateHoldersPanel';

interface Props {
  customerId: string;
  projectId: string;
  customerName: string;
  documents: Array<{ id: string; name: string }>;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Certificates (AGENT_MAILBOX_PLAN.md D10): a COI on the active ACORD 25 for
 * a saved or ad-hoc holder, or a NJ Certificate of Effort from documents.
 */
const CertificateModal: React.FC<Props> = ({ customerId, projectId, customerName, documents, onClose, onCreated }) => {
  const [kind, setKind] = useState<'coi' | 'coe'>('coi');
  const [holders, setHolders] = useState<CertificateHolder[]>([]);
  const [holderId, setHolderId] = useState<string>('');
  const [adhoc, setAdhoc] = useState(emptyHolder());
  const [saveHolder, setSaveHolder] = useState(true);
  const [ops, setOps] = useState('');
  const [docIds, setDocIds] = useState<string[]>([]);
  const [manageHolders, setManageHolders] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const loadHolders = () => holdersApi.list(customerId).then((r) => setHolders(r.holders)).catch(() => setHolders([]));
  useEffect(() => {
    loadHolders();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'coi') {
        let useHolderId = holderId || null;
        if (!useHolderId && saveHolder && adhoc.name.trim()) {
          const { holder } = await holdersApi.create(customerId, adhoc);
          useHolderId = holder.id;
        }
        const r = await acordApi.coi({ customerId, projectId, holderId: useHolderId, holder: useHolderId ? null : adhoc, descriptionOfOperations: ops || undefined });
        setDone(`Saved “${r.documentName}”${r.flattenedDocumentId ? ' plus a final (flattened) copy for sending' : ''}.${r.unresolvedKeys.length ? ` ${r.unresolvedKeys.length} field(s) had no data on file.` : ''}`);
      } else {
        const r = await acordApi.coe({ customerId, projectId, documentIds: docIds });
        setDone(`Saved “${r.documentName}”.`);
      }
      onCreated();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not generate the certificate'));
    } finally {
      setBusy(false);
    }
  };

  const ready = kind === 'coi' ? !!holderId || !!adhoc.name.trim() : docIds.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Certificate — {customerName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">✕</button>
        </div>
        {manageHolders ? (
          <div className="mt-4">
            <CertificateHoldersPanel customerId={customerId} onClose={() => { setManageHolders(false); loadHolders(); }} />
          </div>
        ) : done ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-green-700 dark:text-green-400">{done}</p>
            <button className={btn.primary} onClick={onClose}>Done</button>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex gap-2">
              <button className={kind === 'coi' ? btn.primary : btn.secondary} onClick={() => setKind('coi')}>Certificate of insurance (ACORD 25)</button>
              <button className={kind === 'coe' ? btn.primary : btn.secondary} onClick={() => setKind('coe')}>Certificate of Effort (NJ)</button>
            </div>
            {kind === 'coi' ? (
              <>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Certificate holder</span>
                    <button className="text-xs text-primary hover:underline" onClick={() => setManageHolders(true)}>Manage saved holders</button>
                  </div>
                  <select className={input} value={holderId} onChange={(e) => setHolderId(e.target.value)}>
                    <option value="">New holder…</option>
                    {holders.map((h) => <option key={h.id} value={h.id}>{h.name}{h.city ? ` — ${h.city}` : ''}</option>)}
                  </select>
                </div>
                {!holderId && (
                  <div className="space-y-2">
                    <HolderForm value={adhoc} onChange={setAdhoc} />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                      <input type="checkbox" checked={saveHolder} onChange={(e) => setSaveHolder(e.target.checked)} /> Save this holder for next time
                    </label>
                  </div>
                )}
                <label className="block text-xs">
                  <span className="text-gray-500">Description of operations / locations / vehicles</span>
                  <textarea className={input} rows={3} value={ops} onChange={(e) => setOps(e.target.value)} placeholder={holders.find((h) => h.id === holderId)?.descriptionOfOperations || 'e.g. Re: Project at 12 Main St. Certificate holder is additional insured as required by written contract.'} />
                </label>
              </>
            ) : (
              <div>
                <span className="text-xs text-gray-500">Documents showing the declinations / quotes</span>
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded mt-1 divide-y divide-gray-100 dark:divide-gray-700">
                  {documents.length === 0 && <p className="text-sm text-gray-500 p-2">No documents in this folder.</p>}
                  {documents.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                      <input type="checkbox" checked={docIds.includes(d.id)} onChange={(e) => setDocIds((ids) => (e.target.checked ? [...ids, d.id] : ids.filter((x) => x !== d.id)))} />
                      <span className="truncate">{d.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
            <div className="flex justify-end gap-2">
              <button className={btn.secondary} onClick={onClose}>Cancel</button>
              <button className={btn.primary} onClick={generate} disabled={busy || !ready}>{busy ? 'Generating…' : 'Generate'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificateModal;
